import {
  getFontCss,
  HoverableLink,
  Processing,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import MoreDropdown, {
  MoreDropdownDesigns,
} from 'src/components/MoreDropdown/MoreDropdown'
import SvgAttachmentFile from 'src/components/Svgs/SvgAttachmentFile'
import SvgChevronDownLoD from 'src/components/Svgs/SvgChevronDownLoD'
import SvgChevronUpLoD from 'src/components/Svgs/SvgChevronUpLoD'
import SvgDownload from 'src/components/Svgs/SvgDownload'
import TextWithTooltipOnClick, {
  TextWithTooltipOnClickSize,
} from 'src/components/TextWithTooltipOnClick/TextWithTooltipOnClick'
import { useTranslate } from 'src/localization/Trns'
import { css } from 'styled-components'

export interface CsvInputFieldTemplateOption<T> {
  key: T
  labelSrc: string
  onClick: () => void
  testId?: string
}

interface CsvInputFieldTemplateOptionsProps<T extends Record<string, string>> {
  templateOptions: Array<CsvInputFieldTemplateOption<keyof T>>
}

const CsvInputFieldTemplateOptions = <T extends Record<string, string>>(
  props: CsvInputFieldTemplateOptionsProps<T>,
): React.ReactElement<CsvInputFieldTemplateOptionsProps<T>> => {
  const translate = useTranslate()

  const TextTooltip: React.ReactElement = (
    <span
      css={css`
        position: relative;
        display: flex;
        flex-direction: row;
        align-items: center;
      `}
    >
      <span
        css={css`
          padding-right: 5px;
        `}
      >
        <TextWithTooltipOnClick
          text={translate('Choose a template to download')}
          underline
          openLeft
          hover
          size={TextWithTooltipOnClickSize.Medium}
          tooltipSize={TextWithTooltipOnClickSize.Medium}
          disabled
        />
      </span>
    </span>
  )

  if (props.templateOptions.length === 0) {
    return <></>
  } else if (props.templateOptions.length === 1) {
    const option = props.templateOptions[0]
    return (
      <HoverableLink
        css={css`
          display: flex;
          align-items: center;
        `}
        onClick={(): void => option.onClick()}
        ariaLabelSource={'Download template'}
        testId={'downloadTemplateBtn'}
      >
        {translate('Download template')}
        <SvgDownload
          css={css`
            transform: scale(calc(14 / 24));
          `}
        />
      </HoverableLink>
    )
  } else {
    return (
      <MoreDropdown
        design={MoreDropdownDesigns.CtaBlueWhiteBg}
        topOptions={props.templateOptions.map((option) => ({
          key: option.key.toString(),
          label: translate(option.labelSrc),
          onClick: option.onClick,
          ariaLabelSource: 'Download template',
          testId: option.testId,
        }))}
        bottomOptions={[]}
        descriptionTextComponent={TextTooltip}
        openComponent={<SvgChevronUpLoD />}
        closedComponent={<SvgChevronDownLoD />}
      />
    )
  }
}

interface CsvInputFieldProps<T extends Record<string, string>> {
  loadingBulkTemplate?: boolean
  errors?: Array<React.ReactElement | string>
  value?: File
  className?: string
  onDrop: (files: File[]) => void
  templateOptions: Array<CsvInputFieldTemplateOption<keyof T>>
  showConvertToCSVMessage?: boolean
}

const CsvInputField = <T extends Record<string, string>>(
  props: CsvInputFieldProps<T>,
): React.ReactElement<CsvInputFieldProps<T>> => {
  const translate = useTranslate()

  const [showErrors, setShowErrors] = useState(props.errors !== undefined)

  useEffect((): void => {
    setShowErrors(props.errors !== undefined)
  }, [props.errors])

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: props.onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
    },
  })

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
      `}
      className={props.className}
    >
      <FieldLabel
        dontHideOverflow
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <span>{translate('CSV')}</span>
        <div
          css={css`
            position: relative;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
          `}
        >
          {props.loadingBulkTemplate ? (
            <HoverableLink
              css={css`
                display: flex;
                align-items: center;
                gap: 5px;
              `}
              ariaLabelSource={'Downloading...'}
              testId={'downloadingTemplateIndicator'}
              disabled={true}
            >
              {translate('Downloading...')}
              <Processing />
            </HoverableLink>
          ) : (
            <CsvInputFieldTemplateOptions
              templateOptions={props.templateOptions}
            />
          )}
        </div>
      </FieldLabel>
      <div
        data-testid={'csvUploadBox'}
        {...getRootProps()}
        css={css`
          cursor: pointer;
          width: 100%;
          border-radius: 6px;
          border: 1px solid ${styleConstants.colors.gray_medium};
          ${getFontCss(
            14,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
          background-color: ${styleConstants.colors.gray_lightest};

          &:focus,
          &:hover {
            background-color: ${styleConstants.colors.white};
            border: 1px solid ${themeConstants.colors.cta};
          }
        `}
      >
        <input {...getInputProps()} />
        {props.value === undefined && (
          <div
            css={css`
              padding: 50px 0px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
            `}
          >
            <span
              css={css`
                width: 250px;
                text-align: center;
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_dark,
                  styleConstants.fontFamilies.regular,
                )};
                margin-bottom: 10px;
              `}
            >
              {translate(
                props.showConvertToCSVMessage
                  ? 'Drag and drop your CSV file here (be sure to export your XLSX as a CSV), or'
                  : 'Drag and drop your CSV file here, or',
              )}
            </span>
            <Button
              css={css`
                flex-shrink: 0;
              `}
              design={ButtonDesigns.designCtaInverse}
              type={ButtonTypes.button}
              text={translate('Select a File to Upload')}
              testId={'fileUploadBtn'}
            />
          </div>
        )}
        {props.value !== undefined && (
          <div
            css={css`
              padding: 20px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <div
              css={css`
                display: flex;
                justify-content: center;
              `}
            >
              <SvgAttachmentFile />
              <span
                css={css`
                  width: 250px;
                  padding-left: 10px;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {props.value.name}
              </span>
            </div>
            <Button
              css={css`
                flex-shrink: 0;
              `}
              design={ButtonDesigns.designCtaInverse}
              type={ButtonTypes.button}
              text={translate('Choose a Different File')}
              testId={'changeFileBtn'}
            />
          </div>
        )}
      </div>
      {props.errors !== undefined && showErrors && (
        <ErrorList errors={props.errors} />
      )}
    </div>
  )
}

export default React.memo(CsvInputField)
